import request from "@/providers/requests";
import ENDPOINTS from "@/providers/end-points";
import { TabsByStatusTimeCounterType } from "@/types/dashboard/tabsByStatusTimeCounter";
import { Response } from "@/interface/response.interface";
import TabsByStatusTimeCounter from "@/modules/dashboard/tabs-by-status-time-counter";
import LocationOrdersNumber from "@/modules/dashboard/location-orders-number";
import { LocationOrdersNumberType } from "@/types/dashboard/locationOrdersNumber";
import { TopSellingItemType } from "@/types/dashboard/topSellingItem";
import TopSellingItems from "@/modules/dashboard/top-selling-items";
import { TopSellingAgentType } from "@/types/dashboard/topSellingAgent";
import { TopSellingAreaType } from "@/types/dashboard/topSellingArea";
import TopSellingArea, { TopSellingAreaInterface } from "@/modules/dashboard/top-selling-area";
import TopSellingAgent, { TopSellingAgentInterface } from "@/modules/dashboard/top-selling-agent";
import { LocationDeliveryTimeType } from "@/types/dashboard/locationDeliveryTime";
import LocationDeliveryTime from "@/modules/dashboard/location-delivery-time";
import { SalesIndicatorsType } from "@/types/dashboard/salesIndicators";
import SalesIndicators from "@/modules/dashboard/sales-indicators";

import {
  LocationDeliveryTimeInterface,
  LocationOrdersNumberInterface,
  TopSellingItemsInterface,
  SalesIndicatorsInterface,
  TabsByStatusTimeCounterInterface,
} from "@/interface/dashboard";

const Dashboard = {
  namespaced: true,

  state: () => ({
    apiTabsByStatusTimeCounterData: null,
    apiLocationOrdersNumberData: null,
    apiTopSellingItemsData: null,
    apiTopSellingAreaData: null,
    apiTopSellingAgentData: null,
    apiLocationDeliveryTimeData: null,
    apiSalesIndicatorsData: null,
  }),

  getters: {
    apiTabsByStatusTimeCounterData: (state: any): TabsByStatusTimeCounterInterface =>
      state.apiTabsByStatusTimeCounterData,
    apiLocationOrdersNumberData: (state: any): LocationOrdersNumberInterface => state.apiLocationOrdersNumberData,
    apiTopSellingItemsData: (state: any): TopSellingItemsInterface => state.apiTopSellingItemsData,
    apiTopSellingAreaData: (state: any): TopSellingAreaInterface => state.apiTopSellingAreaData,
    apiTopSellingAgentData: (state: any): TopSellingAgentInterface => state.apiTopSellingAgentData,
    apiLocationDeliveryTimeData: (state: any): LocationDeliveryTimeInterface => state.apiLocationDeliveryTimeData,
    apiSalesIndicatorsData: (state: any): SalesIndicatorsInterface => state.apiSalesIndicatorsData,
  },

  mutations: {
    SET_TABS_BY_STATUS_TIME_COUNTER_DATA(state: any, payload: any) {
      state.apiTabsByStatusTimeCounterData = payload;
    },
    SET_LOCATION_ORDERS_NUMBER_DATA(state: any, payload: any) {
      state.apiLocationOrdersNumberData = payload;
    },
    SET_TOP_SELLING_ITEMS_DATA(state: any, payload: any) {
      state.apiTopSellingItemsData = payload;
    },
    SET_TOP_SELLING_AREA_DATA(state: any, payload: any) {
      state.apiTopSellingAreaData = payload;
    },

    SET_TOP_SELLING_AGENT_DATA(state: any, payload: any) {
      state.apiTopSellingAgentData = payload;
    },

    SET_LOCATION_DELIVERY_TIME_DATA(state: any, payload: any) {
      state.apiLocationDeliveryTimeData = payload;
    },
    SET_SALES_INDICATORS_DATA(state: any, payload: any) {
      state.apiSalesIndicatorsData = payload;
    },
  },

  actions: {
    async getTabsByStatusTimeCounter(
      { commit }: { commit: any },
      { location_list, creation_time_from, creation_time_to, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<TabsByStatusTimeCounterType>>>(
          `${ENDPOINTS.TABS_BY_STATUS_TIME_COUNTER({
            location_list,
            creation_time_from,
            creation_time_to,
            status,
            source,
          })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_TABS_BY_STATUS_TIME_COUNTER_DATA", new TabsByStatusTimeCounter(data));
        })
        .catch(async (error) => {
          await commit("SET_TABS_BY_STATUS_TIME_COUNTER_DATA", new TabsByStatusTimeCounter());
        });
    },

    async getLocationOrdersNumber(
      { commit }: { commit: any },
      { locationsIds, begin_time, end_time, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<LocationOrdersNumberType>>>(
          `${ENDPOINTS.LOCATION_ORDERS_NUMBER({ locationsIds, begin_time, end_time, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_LOCATION_ORDERS_NUMBER_DATA", new LocationOrdersNumber(data));
        })
        .catch(async (error) => {
          await commit("SET_LOCATION_ORDERS_NUMBER_DATA", new LocationOrdersNumber());
        });
    },

    async getTopSellingItems(
      { commit }: { commit: any },
      { locationsIds, begin_time, end_time, page_size = 5, page = 1, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<TopSellingItemType>>>(
          `${ENDPOINTS.TOP_SELLING_ITEMS({ locationsIds, begin_time, end_time, page_size, page, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_TOP_SELLING_ITEMS_DATA", new TopSellingItems(data));
        })
        .catch(async (error) => {
          await commit("SET_TOP_SELLING_ITEMS_DATA", new TopSellingItems());
        });
    },

    async getTopSellingArea(
      { commit }: { commit: any },
      { locationsIds, begin_time, end_time, page_size = 5, page = 1, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<TopSellingAreaType>>>(
          `${ENDPOINTS.TOP_SELLING_AREA({ locationsIds, begin_time, end_time, page_size, page, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_TOP_SELLING_AREA_DATA", new TopSellingArea(data));
        })
        .catch(async (error) => {
          await commit("SET_TOP_SELLING_AREA_DATA", new TopSellingArea());
        });
    },

    async getTopSellingAgent(
      { commit }: { commit: any },
      { location_id, begin_time, end_time, page_size = 5, page = 1, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<TopSellingAgentType>>>(
          `${ENDPOINTS.TOP_SELLING_AGENT({ location_id, begin_time, end_time, page_size, page, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_TOP_SELLING_AGENT_DATA", new TopSellingAgent(data));
        })
        .catch(async (error) => {
          await commit("SET_TOP_SELLING_AGENT_DATA", new TopSellingAgent());
        });
    },

    async getLocationDeliveryTime(
      { commit }: { commit: any },
      { locationsIds, begin_time, end_time, page_size = 5, page = 1, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<LocationDeliveryTimeType>>>(
          `${ENDPOINTS.LOCATION_DELIVERY_TIME({ locationsIds, begin_time, end_time, page_size, page, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_LOCATION_DELIVERY_TIME_DATA", new LocationDeliveryTime(data));
        })
        .catch(async (error) => {
          await commit("SET_LOCATION_DELIVERY_TIME_DATA", new LocationDeliveryTime());
        });
    },

    async getSalesIndicators(
      { commit }: { commit: any },
      { locationsIds, begin_time, end_time, page_size = 5, page = 1, status = "", source = "" }
    ) {
      return await request
        .get<Response<Array<SalesIndicatorsType>>>(
          `${ENDPOINTS.SALES_INDICATORS({ locationsIds, begin_time, end_time, page_size, page, status, source })}`
        )
        .then((response) => response.data)
        .then(async ({ data }) => {
          await commit("SET_SALES_INDICATORS_DATA", new SalesIndicators(data));
        })
        .catch(async (error) => {
          await commit("SET_SALES_INDICATORS_DATA", new SalesIndicators());
        });
    },
  },
};

export default Dashboard;
